/* eslint-disable no-unused-vars */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  Building2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ArrowLeft,
  Plus,
  RefreshCw,
  Archive,
  ExternalLink,
  Loader2,
  HardHat,
  ShieldCheck,
  Hourglass,
  FileSignature,
  FolderArchive,
  FileText,
  Upload,
  Scale,
  Calculator,
} from 'lucide-react';



export const CcmiComplianceDialog = ({ ctx }) => {
  const {
    analyzingDocId, archiving, assigningJuriste, autosaveAt, autosaveError, autosaveStatus,
    autosaveTick, complianceDraft, complianceLoading, complianceSaving, compliancingDoc, counts,
    creating, creatingFinalDevis, draft, filterStatut, finalContractPaywall,
    generatingFinalContract, generatingPdf, importingPdf, items, juristeAssignLoading,
    juristeCommentInput, juristeEmailInput, loadError, loading, reportingDocId, reviewingDocId,
    search, selected, showCreate, syncing,
    setAnalyzingDocId, setArchiving, setAssigningJuriste, setAutosaveAt, setAutosaveError,
    setAutosaveStatus, setAutosaveTick, setComplianceDraft, setComplianceLoading, setComplianceSaving,
    setCompliancingDoc, setCounts, setCreating, setCreatingFinalDevis, setDraft,
    setFilterStatut, setFinalContractPaywall, setGeneratingFinalContract, setGeneratingPdf,
    setImportingPdf, setItems, setJuristeAssignLoading, setJuristeCommentInput, setJuristeEmailInput,
    setLoadError, setLoading, setReportingDocId, setReviewingDocId, setSearch, setSelected,
    setShowCreate, setSyncing,
    autosaveBaselineRef, autosaveTimerRef, fetchList, fetchedOnce, fileInputRef, handleAnalyzeDoc,
    handleArchive, handleCreate, handleCreateFinalContractDevis, handleDownloadReviewReport,
    handleGenerateFinalContract, handleGeneratePdf, handleImportPdf, handleReviewAi, handleSync,
    navigate, openComplianceModal, openJuristeAssign, saveComplianceDraft, submitComplianceDraft,
    submitJuristeAssign, triggerAutosave,
    STATUT_META, GUARD_STATUS_META, getAuthHeader, CCMI_COMPLIANCE_CHECKLIST, computeComplianceStatus,
    ComplianceStatusPanel, AutosaveIndicator,
  } = ctx;
  return (
      <Dialog open={!!compliancingDoc} onOpenChange={(open) => { if (!open) { setCompliancingDoc(null); setComplianceDraft(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ccmi-compliance-modal">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Mise en conformité CCMI — Projet
            </DialogTitle>
            <DialogDescription>
              Génération d&apos;un <b>projet</b> de contrat corrigé à partir des manquements détectés par l&apos;IA.
              Le PDF original importé <b>reste intact</b>. Validation juridique requise avant tout usage.
            </DialogDescription>
          </DialogHeader>
          {compliancingDoc && complianceDraft && (
            <div className="space-y-4">
              {/* V38.18 — Indicateur d'autosave silencieux */}
              <AutosaveIndicator
                status={autosaveStatus}
                savedAt={autosaveAt}
                error={autosaveError}
                tick={autosaveTick}
              />

              {/* Rappel manquements IA */}
              {compliancingDoc.doc.ai_analysis?.result?.manquements?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-3" data-testid="ccmi-compliance-ai-manquements">
                  <p className="text-sm font-semibold text-red-800 mb-1">Manquements IA à corriger :</p>
                  <ul className="list-disc ml-5 text-xs text-red-700 space-y-0.5">
                    {compliancingDoc.doc.ai_analysis.result.manquements.slice(0, 8).map((m, i) => (
                      <li key={i}>{m}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* V38.16 — Statut de conformité en temps réel */}
              <ComplianceStatusPanel draft={complianceDraft} />

              {/* Bloc Maitre d'ouvrage */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Maître d&apos;ouvrage</h4>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Nom" value={complianceDraft.maitre_ouvrage?.nom || ''}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, maitre_ouvrage: { ...complianceDraft.maitre_ouvrage, nom: e.target.value } })}
                    data-testid="ccmi-compli-mo-nom" />
                  <Input placeholder="Prénom" value={complianceDraft.maitre_ouvrage?.prenom || ''}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, maitre_ouvrage: { ...complianceDraft.maitre_ouvrage, prenom: e.target.value } })} />
                  <Input placeholder="Email" value={complianceDraft.maitre_ouvrage?.email || ''}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, maitre_ouvrage: { ...complianceDraft.maitre_ouvrage, email: e.target.value } })} />
                  <Input placeholder="Téléphone" value={complianceDraft.maitre_ouvrage?.telephone || ''}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, maitre_ouvrage: { ...complianceDraft.maitre_ouvrage, telephone: e.target.value } })} />
                  <Input className="col-span-2" placeholder="Adresse" value={complianceDraft.maitre_ouvrage?.adresse || ''}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, maitre_ouvrage: { ...complianceDraft.maitre_ouvrage, adresse: e.target.value } })} />
                </div>
              </div>

              {/* Bloc Constructeur */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Constructeur</h4>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Raison sociale *" value={complianceDraft.constructeur?.raison_sociale || ''}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, constructeur: { ...complianceDraft.constructeur, raison_sociale: e.target.value } })}
                    data-testid="ccmi-compli-constructeur-rs" />
                  <Input placeholder="SIRET" value={complianceDraft.constructeur?.siret || ''}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, constructeur: { ...complianceDraft.constructeur, siret: e.target.value } })} />
                  <Input className="col-span-2" placeholder="Garant (garantie livraison)" value={complianceDraft.constructeur?.garant || ''}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, constructeur: { ...complianceDraft.constructeur, garant: e.target.value } })} />
                </div>
              </div>

              {/* Bloc Terrain */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Terrain</h4>
                <div className="grid grid-cols-2 gap-2">
                  <Input className="col-span-2" placeholder="Adresse *" value={complianceDraft.terrain?.adresse || ''}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, terrain: { ...complianceDraft.terrain, adresse: e.target.value } })}
                    data-testid="ccmi-compli-terrain-adresse" />
                  <Input placeholder="Surface (m²)" type="number" value={complianceDraft.terrain?.surface_m2 || ''}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, terrain: { ...complianceDraft.terrain, surface_m2: e.target.value } })} />
                  <Input placeholder="Parcelle cadastrale" value={complianceDraft.terrain?.parcelle || ''}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, terrain: { ...complianceDraft.terrain, parcelle: e.target.value } })} />
                </div>
              </div>

              {/* Bloc Conditions financieres et techniques */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Conditions financières & techniques</h4>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Prix TTC * (€)" type="number" value={complianceDraft.prix_ttc}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, prix_ttc: e.target.value })}
                    data-testid="ccmi-compli-prix-ttc" />
                  <Select value={complianceDraft.type_contrat} onValueChange={(v) => setComplianceDraft({ ...complianceDraft, type_contrat: v })}>
                    <SelectTrigger data-testid="ccmi-compli-type-contrat"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="avec_plan">Avec fourniture de plan</SelectItem>
                      <SelectItem value="sans_plan">Sans fourniture de plan</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="Délai d'exécution" value={complianceDraft.delai_execution}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, delai_execution: e.target.value })} />
                  <Input placeholder="Pénalités de retard" value={complianceDraft.penalites_retard}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, penalites_retard: e.target.value })} />
                  <Input placeholder="Financement (prêts)" value={complianceDraft.financement}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, financement: e.target.value })} />
                  <Input placeholder="Permis de construire" value={complianceDraft.permis}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, permis: e.target.value })} />
                  <Input placeholder="Assurance Dommages-Ouvrage" value={complianceDraft.assurance_do}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, assurance_do: e.target.value })} />
                  <Input placeholder="Garantie de livraison" value={complianceDraft.garantie_livraison}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, garantie_livraison: e.target.value })} />
                  <Input className="col-span-2" placeholder="Notice / Plans / Annexes" value={complianceDraft.notice_plans_annexes}
                    onChange={(e) => setComplianceDraft({ ...complianceDraft, notice_plans_annexes: e.target.value })} />
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold text-slate-700">Clauses particulières ajoutées</Label>
                <Textarea rows={3} value={complianceDraft.clauses_particulieres}
                  onChange={(e) => setComplianceDraft({ ...complianceDraft, clauses_particulieres: e.target.value })}
                  data-testid="ccmi-compli-clauses" />
              </div>

              <p className="text-[11px] text-amber-700 italic">
                ⚠ Ce projet de mise en conformité ne constitue pas un contrat définitif. Validation juridique requise.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCompliancingDoc(null); setComplianceDraft(null); }} disabled={complianceLoading || complianceSaving}>
              Annuler
            </Button>
            {/* V38.17 — Bouton de sauvegarde explicite sans PDF */}
            <Button
              onClick={saveComplianceDraft}
              disabled={complianceSaving || complianceLoading}
              variant="secondary"
              className="bg-slate-100 hover:bg-slate-200 text-slate-900 border border-slate-300"
              data-testid="ccmi-compli-save-btn"
            >
              {complianceSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Enregistrer
            </Button>
            <Button
              onClick={submitComplianceDraft}
              disabled={complianceLoading || complianceSaving}
              className="bg-red-700 hover:bg-red-800"
              data-testid="ccmi-compli-submit-btn"
            >
              {complianceLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />}
              Générer projet mis en conformité
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
};

export default CcmiComplianceDialog;
